import React, { useState, useEffect } from 'react';
import i18n from '../../i18n';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Slider,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  FilterList as FilterListIcon,
  Build as BuildIcon,
  Payment as PaymentIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../../context/CategoryContext';

// Hook personnalisé pour charger les vraies catégories
const useRealCategories = () => {
  const { categoriesList } = useCategories();
  
  return React.useMemo(() => {
    return categoriesList?.map(category => category.name) || [];
  }, [categoriesList]);
};



// Fonction de fallback uniquement si TOUTES les APIs échouent
const getFallbackCities = (countryCode) => {
  console.warn(`Utilisation du fallback minimal pour ${countryCode} - toutes les APIs ont échoué`);
  
  // Fallback minimal uniquement avec les capitales principales
  const minimalFallbacks = {
    
    'ES': ['Madrid', 'Barcelona', 'Valencia'],
    'IT': ['Rome', 'Milan', 'Naples']
  };
  
  const cities = minimalFallbacks[countryCode] || [];
  console.warn(`Fallback d'urgence: ${cities.length} villes capitales pour ${countryCode}`);
  
  return cities;
};

// Fonction utilitaire pour convertir le code pays en nom de pays
const getCountryNameFromCode = (countryCode) => {
  const countryNames = {
    
    'ES': 'Spain',
    'IT': 'Italy'
  };
  return countryNames[countryCode] || countryCode;
};

// Fonction pour charger les villes via de vraies APIs publiques avec gestion intelligente
const loadCitiesForCountry = async (countryCode, limit = 100) => {
  try {
    console.log(`Tentative de chargement de ${limit} villes pour ${countryCode} via API...`);
    
    // Option 1: Utiliser GeoNames API (gratuite avec inscription) avec limitation
    const geonamesUsername = 'your_username'; // À remplacer par votre nom d'utilisateur GeoNames
    
    if (geonamesUsername !== 'your_username') {
      const geonamesUrl = `http://api.geonames.org/searchJSON?country=${countryCode}&featureClass=P&maxRows=${limit}&orderby=population&username=${geonamesUsername}`;
      
      const response = await fetch(geonamesUrl);
      if (response.ok) {
        const data = await response.json();
        if (data.geonames && data.geonames.length > 0) {
          const cities = data.geonames
            .map(city => ({
              name: city.name,
              population: city.population || 0,
              lat: city.lat,
              lng: city.lng
            }))
            .filter(city => city.name && city.name.length > 1)
            .sort((a, b) => b.population - a.population) // Trier par population
            .slice(0, limit)
            .map(city => city.name);
          
          console.log(`GeoNames API: ${cities.length} villes chargées pour ${countryCode} (triées par population)`);
          return cities;
        }
      }
    }
    
    // Option 2: Utiliser CountriesNow API avec limitation
    try {
      const citiesResponse = await fetch(`https://countriesnow.space/api/v0.1/countries/cities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          country: getCountryNameFromCode(countryCode)
        })
      });
      
      if (citiesResponse.ok) {
        const citiesData = await citiesResponse.json();
        if (citiesData.data && citiesData.data.length > 0) {
          // Limiter et trier alphabétiquement
          const cities = citiesData.data
            .filter(city => city && city.length > 1)
            .sort()
            .slice(0, limit); // Limiter le nombre de villes
          
          console.log(`CountriesNow API: ${cities.length} villes chargées pour ${countryCode} (limitées à ${limit})`);
          return cities;
        }
      }
    } catch (apiError) {
      console.warn('Erreur API CountriesNow:', apiError);
    }
    
    // Option 3: OpenDataSoft avec limitation et tri par population
    try {
      const opendataUrl = `https://public.opendatasoft.com/api/records/1.0/search/?dataset=geonames-all-cities-with-a-population-1000&q=&facet=country_code&refine.country_code=${countryCode}&rows=${limit}&sort=population&order=desc`;
      
      const response = await fetch(opendataUrl);
      if (response.ok) {
        const data = await response.json();
        if (data.records && data.records.length > 0) {
          const cities = data.records
            .map(record => record.fields.name)
            .filter(name => name && name.length > 1)
            .slice(0, limit);
          
          console.log(`OpenDataSoft API: ${cities.length} villes chargées pour ${countryCode} (triées par population, limitées à ${limit})`);
          return cities;
        }
      }
    } catch (apiError) {
      console.warn('Erreur API OpenDataSoft:', apiError);
    }
    
    // Si toutes les APIs échouent, lever une erreur
    throw new Error('Toutes les APIs de villes ont échoué');
    
  } catch (error) {
    console.error(`Erreur lors du chargement des villes pour ${countryCode}:`, error);
    return [];
  }
};

const UserPreferences = () => {
  const { t } = useTranslation();
  const { user, updateUserPreferences } = useAuth();
  
  // Utiliser les vraies catégories
  const realCategories = useRealCategories();
  
  // États pour la sélection de pays et villes (comme dans AdvancedFilters)
  const [countries, setCountries] = React.useState([]);
  const [cities, setCities] = React.useState({});
  const [selectedCountry, setSelectedCountry] = React.useState('');
  const [countryCities, setCountryCities] = React.useState([]);
  const [isLoadingCities, setIsLoadingCities] = React.useState(false);
  const [isLoadingCountries, setIsLoadingCountries] = React.useState(false);
  const [selectedCities, setSelectedCities] = React.useState([]);

  // État initial des préférences adapté à une plateforme de location d'outils
  const [preferences, setPreferences] = useState({
    notifications: {
      email: true,
      push: true,
      sms: false,
      rentalReminders: true,
      returnReminders: true,
      newToolsInArea: true,
      priceAlerts: false
    },
    privacy: {
      showProfile: true,
      showContactInfo: false,
      allowMessaging: true,
      shareRentalHistory: false
    },
    search: {
      defaultRadius: 10,
      savedLocations: [],
      preferredCategories: [],
      resultsPerPage: 10
    },
    rental: {
      preferredPaymentMethod: 'cash',
      defaultRentalDuration: 1,
      automaticExtensionRequest: false,
      showUnavailableDates: true
    },
    language: 'fr'
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Charger les pays dynamiquement via l'API
  const loadCountries = React.useCallback(async () => {
    try {
      setIsLoadingCountries(true);
      console.log('Chargement des pays via API...');
      
      // Option 1: RestCountries API (gratuite et fiable)
      const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2,flag');
      
      if (response.ok) {
        const countriesData = await response.json();
        
        // Formater les données pour notre usage
        const formattedCountries = {};
        countriesData.forEach(country => {
          if (country.cca2 && country.name?.common) {
            formattedCountries[country.cca2] = {
              name: country.name.common,
              flag: country.flag || '🏴',
              cities: []
            };
          }
        });
        
        console.log(`API: ${Object.keys(formattedCountries).length} pays chargés`);
        setCities(formattedCountries);
        setCountries(Object.keys(formattedCountries));
        return;
      }
    } catch (error) {
      console.warn('Erreur chargement pays dynamique, utilisation du fallback:', error);
      
      // Fallback avec quelques pays principaux
      const fallbackCountries = {
        
        'ES': { name: 'Espagne', flag: '🇪🇸', cities: [] },
        'IT': { name: 'Italie', flag: '🇮🇹', cities: [] }
      };
      
      setCities(fallbackCountries);
      setCountries(Object.keys(fallbackCountries));
    } finally {
      setIsLoadingCountries(false);
    }
  }, []);

  // Charger les pays au démarrage
  React.useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  // Charger les préférences de l'utilisateur au chargement du composant
  useEffect(() => {
    if (user?.preferences) {
      // Fusion des préférences existantes avec notre structure par défaut
      setPreferences(prev => {
        const userPrefs = user.preferences;

        const updatedPrefs = {
          notifications: {
            ...prev.notifications,
            ...(userPrefs.notifications || {}),
            // Ajouter les nouvelles préférences de notification spécifiques
            rentalReminders: userPrefs.notifications?.rentalReminders ?? true,
            returnReminders: userPrefs.notifications?.returnReminders ?? true,
            newToolsInArea: userPrefs.notifications?.newToolsInArea ?? true,
            priceAlerts: userPrefs.notifications?.priceAlerts ?? false
          },
          privacy: {
            ...prev.privacy,
            ...(userPrefs.privacy || {}),
            shareRentalHistory: userPrefs.privacy?.shareRentalHistory ?? false
          },
          search: {
            ...prev.search,
            ...(userPrefs.search || {}),
            preferredCategories: userPrefs.search?.preferredCategories ||
                userPrefs.search?.categories || [],
            savedLocations: userPrefs.search?.savedLocations || []
          },
          rental: {
            ...prev.rental,
            ...(userPrefs.rental || {}),
            preferredPaymentMethod: userPrefs.rental?.preferredPaymentMethod || 'cash',
            defaultRentalDuration: userPrefs.rental?.defaultRentalDuration || 1
          },
          language: userPrefs.display?.language || userPrefs.language || 'fr'
        };

        // Synchroniser les villes sélectionnées avec les préférences
        setSelectedCities(updatedPrefs.search.savedLocations);

        return updatedPrefs;
      });
    }
  }, [user]);

  // Fonction pour charger les villes d'un pays (copié d'AdvancedFilters)
  const loadCountryCities = React.useCallback(async (countryCode) => {
    if (!countryCode) return;
    
    setIsLoadingCities(true);
    
    try {
      // Vérifier si on a déjà les données en cache
      if (cities[countryCode]?.cities && cities[countryCode].cities.length > 0) {
        console.log(`Utilisation du cache: ${cities[countryCode].cities.length} villes pour ${countryCode}`);
        setCountryCities(cities[countryCode].cities);
        return;
      }
      
      // Charger depuis l'API avec limitation intelligente
      const cityData = await loadCitiesForCountry(countryCode, 200); // Limite à 200 villes max
      
      if (cityData && cityData.length > 0) {
        console.log(`API: ${cityData.length} villes chargées pour ${countryCode}`);
        setCountryCities(cityData);
        
        // Mettre à jour le cache global
        setCities(prev => ({
          ...prev,
          [countryCode]: {
            ...prev[countryCode],
            cities: cityData
          }
        }));
      } else {
        throw new Error('Aucune ville trouvée via API');
      }
    } catch (error) {
      console.warn(`Erreur chargement villes pour ${countryCode}:`, error);
      
      // Fallback avec villes minimales
      const fallbackCities = getFallbackCities(countryCode);
      console.log(`Fallback activé: ${fallbackCities.length} villes pour ${countryCode}`);
      
      setCountryCities(fallbackCities);
      
      // Mettre à jour le cache avec le fallback
      setCities(prev => ({
        ...prev,
        [countryCode]: {
          ...prev[countryCode],
          cities: fallbackCities
        }
      }));
    } finally {
      setIsLoadingCities(false);
    }
  }, [cities]);

  // Gestion du changement de pays
  const handleCountryChange = React.useCallback(async (countryCode) => {
    setSelectedCountry(countryCode);
    setSelectedCities([]); // Réinitialiser les villes sélectionnées
    setCountryCities([]); // Réinitialiser la liste des villes
    
    if (countryCode) {
      await loadCountryCities(countryCode);
    }
  }, [loadCountryCities]);

  // Gestion de la sélection multiple de villes
  const handleCitySelection = React.useCallback((cityNames) => {
    setSelectedCities(cityNames);
    
    // Mettre à jour les préférences
    setPreferences(prev => ({
      ...prev,
      search: {
        ...prev.search,
        savedLocations: cityNames
      }
    }));
  }, []);

  // Gérer les changements dans les préférences de notification
  const handleNotificationChange = (event) => {
    const { name, checked } = event.target;
    setPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [name]: checked
      }
    }));
  };

  // Gérer les changements dans les préférences de confidentialité
  const handlePrivacyChange = (event) => {
    const { name, checked } = event.target;
    setPreferences(prev => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        [name]: checked
      }
    }));
  };

  // Gérer les changements dans les préférences de location
  const handleRentalChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPreferences(prev => ({
      ...prev,
      rental: {
        ...prev.rental,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  // Gérer les changements dans les paramètres de recherche
  const handleSearchSettingChange = (event) => {
    const { name, value } = event.target;
    setPreferences(prev => ({
      ...prev,
      search: {
        ...prev.search,
        [name]: value
      }
    }));
  };

  // Gérer le changement de langue
  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setPreferences(prev => ({
      ...prev,
      language: newLanguage,
      display: {
        ...prev.display,
        language: newLanguage
      }
    }));
  };

  // Gérer le changement du rayon de recherche
  const handleRadiusChange = (event, newValue) => {
    setPreferences(prev => ({
      ...prev,
      search: {
        ...prev.search,
        defaultRadius: newValue
      }
    }));
  };

  // Gérer les changements de catégories préférées
  const handleCategoriesChange = (event, newValues) => {
    setPreferences(prev => ({
      ...prev,
      search: {
        ...prev.search,
        preferredCategories: newValues
      }
    }));
  };

  // Ajouter une localisation sauvegardée (désormais inutile, mais gardé pour compatibilité)
  const addSavedLocation = () => {
    // Cette fonction n'est plus utilisée car nous utilisons la sélection multiple directement
  };

  // Supprimer une localisation sauvegardée
  const removeSavedLocation = (index) => {
    const newLocations = preferences.search.savedLocations.filter((_, i) => i !== index);
    setSelectedCities(newLocations);
    setPreferences(prev => ({
      ...prev,
      search: {
        ...prev.search,
        savedLocations: newLocations
      }
    }));
  };

  // Enregistrer les préférences
  const savePreferences = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      const result = await updateUserPreferences(preferences);

      if (result.success) {
        // S'assurer que la langue i18n correspond à la préférence sauvegardée
        const preferredLanguage = preferences.language || preferences.display?.language || 'fr';
        if (i18n.language !== preferredLanguage) {
          try {
            await i18n.changeLanguage(preferredLanguage);
          } catch (langError) {
            console.warn('Failed to sync language after save:', langError);
          }
        }
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } else {
        setError(result.message || t('userPreferences.errorMessage'));
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour des préférences:', err);
      setError(t('userPreferences.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('userPreferences.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('userPreferences.subtitle')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
        )}

        {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('userPreferences.successMessage')}
            </Alert>
        )}

        <Grid container spacing={4}>
          {/* Section Notifications */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotificationsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="medium">
                  {t('userPreferences.notifications.title')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('userPreferences.notifications.description')}
              </Typography>

              <FormGroup>
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.notifications.email}
                          onChange={handleNotificationChange}
                          name="email"
                      />
                    }
                    label={t('userPreferences.notifications.email')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.notifications.push}
                          onChange={handleNotificationChange}
                          name="push"
                      />
                    }
                    label={t('userPreferences.notifications.push')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.notifications.sms}
                          onChange={handleNotificationChange}
                          name="sms"
                      />
                    }
                    label={t('userPreferences.notifications.sms')}
                />
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  {t('userPreferences.notifications.specificAlerts')}
                </Typography>
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.notifications.rentalReminders}
                          onChange={handleNotificationChange}
                          name="rentalReminders"
                      />
                    }
                    label={t('userPreferences.notifications.rentalReminders')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.notifications.returnReminders}
                          onChange={handleNotificationChange}
                          name="returnReminders"
                      />
                    }
                    label={t('userPreferences.notifications.returnReminders')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.notifications.newToolsInArea}
                          onChange={handleNotificationChange}
                          name="newToolsInArea"
                      />
                    }
                    label={t('userPreferences.notifications.newToolsInArea')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.notifications.priceAlerts}
                          onChange={handleNotificationChange}
                          name="priceAlerts"
                      />
                    }
                    label={t('userPreferences.notifications.priceAlerts')}
                />
              </FormGroup>
            </Box>

            {/* Section Confidentialité */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="medium">
                  {t('userPreferences.privacy.title')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('userPreferences.privacy.description')}
              </Typography>

              <FormGroup>
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.privacy.showProfile}
                          onChange={handlePrivacyChange}
                          name="showProfile"
                      />
                    }
                    label={t('userPreferences.privacy.showProfile')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.privacy.showContactInfo}
                          onChange={handlePrivacyChange}
                          name="showContactInfo"
                      />
                    }
                    label={t('userPreferences.privacy.showContactInfo')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.privacy.allowMessaging}
                          onChange={handlePrivacyChange}
                          name="allowMessaging"
                      />
                    }
                    label={t('userPreferences.privacy.allowMessaging')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.privacy.shareRentalHistory}
                          onChange={handlePrivacyChange}
                          name="shareRentalHistory"
                      />
                    }
                    label={t('userPreferences.privacy.shareRentalHistory')}
                />
              </FormGroup>
            </Box>

            {/* Section Langue */}
            <Box sx={{ mb: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="language-label">{t('userPreferences.language.title')}</InputLabel>
                <Select
                    labelId="language-label"
                    id="language"
                    value={preferences.language}
                    label={t('userPreferences.language.title')}
                    onChange={handleLanguageChange}
                >
                  <MenuItem value="fr">{t('userPreferences.language.french')}</MenuItem>
                  <MenuItem value="ar">{t('userPreferences.language.arabic')}</MenuItem>
                  <MenuItem value="en">{t('userPreferences.language.english')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Section Préférences de Location */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="medium">
                  {t('userPreferences.rental.title')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('userPreferences.rental.description')}
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="payment-method-label">
                  {t('userPreferences.rental.preferredPaymentMethod')}
                </InputLabel>
                <Select
                    labelId="payment-method-label"
                    id="preferredPaymentMethod"
                    name="preferredPaymentMethod"
                    value={preferences.rental.preferredPaymentMethod}
                    label={t('userPreferences.rental.preferredPaymentMethod')}
                    onChange={handleRentalChange}
                >
                  <MenuItem value="cash">{t('userPreferences.rental.cash')}</MenuItem>
                  <MenuItem value="bankTransfer">{t('userPreferences.rental.bankTransfer')}</MenuItem>
                  <MenuItem value="ccp">{t('userPreferences.rental.ccp')}</MenuItem>
                  <MenuItem value="baridi">{t('userPreferences.rental.baridi')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="rental-duration-label">
                  {t('userPreferences.rental.defaultRentalDuration')}
                </InputLabel>
                <Select
                    labelId="rental-duration-label"
                    id="defaultRentalDuration"
                    name="defaultRentalDuration"
                    value={preferences.rental.defaultRentalDuration}
                    label={t('userPreferences.rental.defaultRentalDuration')}
                    onChange={handleRentalChange}
                >
                  <MenuItem value={1}>1 {t('userPreferences.rental.day')}</MenuItem>
                  <MenuItem value={2}>2 {t('userPreferences.rental.days')}</MenuItem>
                  <MenuItem value={3}>3 {t('userPreferences.rental.days')}</MenuItem>
                  <MenuItem value={7}>1 {t('userPreferences.rental.week')}</MenuItem>
                  <MenuItem value={14}>2 {t('userPreferences.rental.weeks')}</MenuItem>
                  <MenuItem value={30}>1 {t('userPreferences.rental.month')}</MenuItem>
                </Select>
              </FormControl>

              <FormGroup>
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.rental.automaticExtensionRequest}
                          onChange={handleRentalChange}
                          name="automaticExtensionRequest"
                          type="checkbox"
                      />
                    }
                    label={t('userPreferences.rental.automaticExtensionRequest')}
                />
                <FormControlLabel
                    control={
                      <Switch
                          checked={preferences.rental.showUnavailableDates}
                          onChange={handleRentalChange}
                          name="showUnavailableDates"
                          type="checkbox"
                      />
                    }
                    label={t('userPreferences.rental.showUnavailableDates')}
                />
              </FormGroup>
            </Box>

            {/* Section Paramètres de Recherche */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="medium">
                  {t('userPreferences.search.title')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('userPreferences.search.description')}
              </Typography>

              {/* Rayon de recherche par défaut */}
              <Typography gutterBottom>
                {t('userPreferences.search.defaultRadius')}: {preferences.search.defaultRadius} km
              </Typography>
              <Slider
                  value={preferences.search.defaultRadius}
                  min={1}
                  max={50}
                  step={1}
                  onChange={handleRadiusChange}
                  valueLabelDisplay="auto"
                  sx={{ mb: 3 }}
              />

              {/* Résultats par page */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="results-label">
                  {t('userPreferences.search.resultsPerPage')}
                </InputLabel>
                <Select
                    labelId="results-label"
                    id="resultsPerPage"
                    name="resultsPerPage"
                    value={preferences.search.resultsPerPage}
                    label={t('userPreferences.search.resultsPerPage')}
                    onChange={handleSearchSettingChange}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>

              {/* Catégories préférées */}
              <Autocomplete
                  multiple
                  id="preferred-categories"
                  options={realCategories}
                  value={preferences.search.preferredCategories}
                  onChange={handleCategoriesChange}
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          variant="outlined"
                          label={t('userPreferences.search.preferredCategories')}
                          placeholder={t('userPreferences.search.selectCategories')}
                          fullWidth
                          margin="normal"
                      />
                  )}
                  renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                          <Chip
                              label={option}
                              {...getTagProps({ index })}
                              color="primary"
                              variant="outlined"
                          />
                      ))
                  }
              />

              {/* Localisations sauvegardées */}
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle1">
                    {t('userPreferences.search.savedLocations')}
                  </Typography>
                </Box>

                {/* Sélection du pays */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="country-select-label">Sélectionner un pays</InputLabel>
                  <Select
                    labelId="country-select-label"
                    value={selectedCountry}
                    label="Sélectionner un pays"
                    onChange={(e) => handleCountryChange(e.target.value)}
                    disabled={isLoadingCountries}
                  >
                    {isLoadingCountries ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Chargement des pays...
                      </MenuItem>
                    ) : (
                      Object.entries(cities).map(([code, data]) => (
                        <MenuItem key={code} value={code}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                width: 20,
                                height: 15,
                                mr: 1,
                                backgroundImage: `url(https://flagcdn.com/w20/${code.toLowerCase()}.png)`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: 0.5,
                                border: '1px solid #ccc'
                              }}
                            />
                            {data.name}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Sélection multiple des villes */}
                {selectedCountry && (
                  <Autocomplete
                    multiple
                    id="cities-multiple-select"
                    options={countryCities}
                    value={selectedCities}
                    onChange={(event, newValue) => handleCitySelection(newValue)}
                    loading={isLoadingCities}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={`Sélectionner des villes ${cities[selectedCountry]?.name ? `en ${cities[selectedCountry].name}` : ''}`}
                        placeholder="Choisissez vos villes préférées..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    }
                    renderOption={(props, option) => (
                      <li {...props}>{option}</li>
                    )}
                  />
                )}

                {/* Affichage des villes sauvegardées existantes */}
                {preferences.search.savedLocations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Villes sauvegardées :
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {preferences.search.savedLocations.map((location, index) => (
                        <Chip
                          key={index}
                          label={location}
                          onDelete={() => removeSavedLocation(index)}
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, textAlign: 'right' }}>
          <Button
              variant="contained"
              color="primary"
              onClick={savePreferences}
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
              size="large"
          >
            {loading ? t('userPreferences.saving') : t('userPreferences.save')}
          </Button>
        </Box>
      </Paper>
  );
};

export default UserPreferences;